import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Alert, Backdrop, CircularProgress, Snackbar, ThemeProvider, StyledEngineProvider } from '@mui/material';
import PublishedOrganizationDocumentsDialog from './components/welcome/PublishedOrganizationDocumentsDialog';
import LanguageChangedDialog from './components/document/dialogs/LanguageChangedDialog';
import AuthenticatedRoute from './routes/AuthenticatedRoute';
import VersionInfoDialog from './components/document/dialogs/VersionInfoDialog';
import ConfirmDialog from './components/ConfirmDialog';
import LoginDialog from './components/header/LoginDialog';
import AllRoutes from './routes/routes';
import UserBar from './components/header/UserBar';
import Header from './components/header/Header';
import Footer from './components/footer/Footer';
import './overrides.css';
import useStore from './store/storeContext';

function App() {
    const { document, user, theming, snackbar, loadingIndicator, dialog, session } = useStore();

    const [ready, setReady] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        async function checkSession() {
            await session.getInitialAppData();
            // Do not make fetch for user details, if user is trying to activate account or change password,
            // because we do not want to navigate to '/', when user is trying to activate or change password.
            const makeFetchForUserDetails = !(
                location.pathname.includes('/index.html/account/activateandreset/') ||
                location.pathname.includes('/index.html/forgot/password/')
            );
            try {
                if (makeFetchForUserDetails) await user.fetchUserDetails();
            } catch (e) {
                session.clearSessionData();
                navigate('/');
            } finally {
                setReady(true);
            }
        }

        void checkSession();

        window.addEventListener('beforeunload', async (event) => {
            try {
                const isChapterLocked = document.isChapterLocked;
                const isPackageLocked = document.isPackageLocked;
                if (isChapterLocked || isPackageLocked) {
                    event.preventDefault();
                    if (isChapterLocked) await document.releaseChapterLocks();
                    if (isPackageLocked) await document.releasePackageLocks();
                }
            } catch (e) {
                return;
            }
        });
    }, []);

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theming.theme}>
                {ready && <AuthenticatedRoute />}
                <Header />
                {user.isUserLoggedIn && <UserBar />}
                <div style={{ flex: '1 0 auto', backgroundColor: theming.shvkPetrol10 }}>
                    <AllRoutes />
                </div>
                <Footer />
                <Snackbar
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    open={snackbar.isOpen}
                    autoHideDuration={6000}
                    onClose={snackbar.close}
                >
                    <Alert severity={snackbar.severity} variant="filled">
                        {snackbar.message}
                    </Alert>
                </Snackbar>
                <Backdrop
                    style={{ color: 'white', zIndex: theming.theme.zIndex.tooltip + 1 }}
                    open={loadingIndicator.isLoading}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
                <ConfirmDialog open={dialog.isDeleteDialogOpen} />
                <LoginDialog open={dialog.isLoginDialogOpen} />
                <PublishedOrganizationDocumentsDialog
                    open={dialog.isPublishedOrganizationDocumentsDialogOpen}
                    onClose={dialog.closePublishedOrganizationDocumentsDialog}
                />
                <VersionInfoDialog isOpen={dialog.isVersionInfoDialogOpen} close={dialog.closeVersionInfoDialog} />
                <LanguageChangedDialog
                    isOpen={dialog.isLanguageChangedDialogOpen}
                    close={dialog.closeLanguageChangedDialog}
                />
            </ThemeProvider>
        </StyledEngineProvider>
    );
}

export default observer(App);
